const SEPARATOR_VISUAL = " · "; // only used to estimate total length
const ELLIPSIS = "…";

function truncateWithEllipsis(text: string, maxChars: number): string {
  if (maxChars <= 0) return "";
  if (text.length <= maxChars) return text;
  if (maxChars === 1) return ELLIPSIS;
  return `${text.slice(0, maxChars - 1)}${ELLIPSIS}`;
}

function calculateFullLength(segments: string[], separator: string): number {
  if (segments.length === 0) return 0;
  const separatorCount = segments.length - 1;
  const contentLength = segments.reduce((sum, seg) => sum + seg.length, 0);
  return contentLength + separatorCount * separator.length;
}

function allocateSegmentMaxes(
  segments: string[],
  availableContent: number,
  minCharsPerSegment = 12
): number[] {
  const n = segments.length;
  const lens = segments.map((s) => s.length);
  const result = new Array<number>(n).fill(0);

  if (n === 0 || availableContent <= 0) return result;

  // Helper: intrinsic minimum for each segment
  const intrinsicMin = (len: number) => Math.min(len, minCharsPerSegment);

  // Precompute intrinsic mins
  const mins = lens.map(intrinsicMin);
  const totalMin = mins.reduce((sum, v) => sum + v, 0);

  // If we don't even have enough for intrinsic mins, just cram as much as possible left-to-right
  if (totalMin > availableContent) {
    let remaining = availableContent;
    for (let i = 0; i < n; i++) {
      if (remaining <= 0) break;
      const give = Math.min(lens[i], remaining);
      result[i] = give;
      remaining -= give;
    }
    return result;
  }

  let remaining = availableContent;

  // Phase 1: greedily fully show from left if we can, while still
  // keeping enough budget to cover intrinsic mins for remaining segments.
  let firstUnfilled = n; // index of first that isn't considered in this phase
  for (let i = 0; i < n; i++) {
    // Sum of intrinsic mins for segments after i
    let minForRest = 0;
    for (let j = i + 1; j < n; j++) {
      minForRest += mins[j];
    }

    const maxForThis = remaining - minForRest;
    if (maxForThis <= 0) {
      firstUnfilled = i;
      break;
    }

    const give = Math.min(lens[i], maxForThis);
    result[i] = give;
    remaining -= give;

    // If this segment is not shown fully, we treat it as "unfilled" for later phases
    if (give < lens[i]) {
      firstUnfilled = i;
      break;
    }
  }

  if (firstUnfilled === n) {
    // All segments ended up full (or we used all budget). Done.
    return result;
  }

  // Collect indices that are not fully handled yet
  const remainingIndices: number[] = [];
  for (let i = firstUnfilled; i < n; i++) {
    remainingIndices.push(i);
  }

  // Phase 2: ensure intrinsic minimums for remaining segments
  for (const i of remainingIndices) {
    const needed = mins[i];
    const already = result[i];
    if (already >= needed) continue;

    const diff = needed - already;
    const give = Math.min(diff, remaining);
    result[i] += give;
    remaining -= give;
  }

  // Phase 3: push remaining characters to bring segments closer to full length
  if (remaining > 0) {
    for (const i of remainingIndices) {
      if (remaining <= 0) break;
      const cap = lens[i] - result[i];
      if (cap <= 0) continue;
      const give = Math.min(cap, remaining);
      result[i] += give;
      remaining -= give;
    }
  }

  return result;
}

export function truncateSegments(
  segments: string[],
  maxLength: number,
  separator: string = SEPARATOR_VISUAL
): string[] {
  const separatorLength = separator.length;

  // Map non-empty segments
  const nonEmptyToOriginal: number[] = [];
  const nonEmptySegments: string[] = [];

  segments.forEach((seg, idx) => {
    if (seg.length > 0) {
      nonEmptyToOriginal.push(idx);
      nonEmptySegments.push(seg);
    }
  });

  if (nonEmptySegments.length === 0) return segments;

  const fullLength = calculateFullLength(nonEmptySegments, separator);

  // If everything fits, return as-is
  if (fullLength <= maxLength) {
    return segments;
  }

  const result = [...segments];

  const joinWithSeparatorCount = (count: number) =>
    Math.max(0, count - 1) * separatorLength;

  // Try progressive truncation from the end
  for (
    let truncateFrom = nonEmptySegments.length - 1;
    truncateFrom >= 0;
    truncateFrom--
  ) {
    const kept = nonEmptySegments.slice(0, truncateFrom);
    const truncated = nonEmptySegments.slice(truncateFrom);

    const keptContentLen = kept.reduce((sum, s) => sum + s.length, 0);
    const keptSepLen = joinWithSeparatorCount(kept.length);

    const hasKept = kept.length > 0;
    const sepBetweenKeptAndTrunc = hasKept ? separatorLength : 0;

    const truncSepLen = joinWithSeparatorCount(truncated.length);

    // Budget for truncated content characters only
    const availableForTruncContent =
      maxLength -
      keptContentLen -
      keptSepLen -
      sepBetweenKeptAndTrunc -
      truncSepLen;

    if (availableForTruncContent < 0) {
      // Even kept alone doesn't fit, continue looping to earlier segments
      continue;
    }

    // If we are truncating all segments
    if (!hasKept) {
      if (availableForTruncContent <= 3) {
        // Show only the first segment, brutally truncated
        const firstIdx = nonEmptyToOriginal[0];
        result[firstIdx] = truncateWithEllipsis(
          nonEmptySegments[0],
          Math.max(1, availableForTruncContent)
        );
        // Clear others
        for (let i = 1; i < nonEmptyToOriginal.length; i++) {
          result[nonEmptyToOriginal[i]] = "";
        }
        return result;
      }

      const maxes = allocateSegmentMaxes(truncated, availableForTruncContent);

      truncated.forEach((_, localIdx) => {
        const globalNonEmptyIdx = truncateFrom + localIdx;
        const origIdx = nonEmptyToOriginal[globalNonEmptyIdx];
        result[origIdx] = truncateWithEllipsis(
          segments[origIdx],
          maxes[localIdx]
        );
      });

      return result;
    }

    // If only last segment is truncated
    if (truncated.length === 1) {
      if (availableForTruncContent >= 4) {
        const lastNonEmptyOriginalIndex = nonEmptyToOriginal[truncateFrom];
        result[lastNonEmptyOriginalIndex] = truncateWithEllipsis(
          segments[lastNonEmptyOriginalIndex],
          availableForTruncContent
        );
        return result;
      }
      // Not enough space to make it worthwhile, try truncating more segments
      continue;
    }

    // Truncating multiple segments at the end
    const minCharsPerSegment = 3;
    const minContentNeeded = truncated.length * minCharsPerSegment;

    if (availableForTruncContent >= minContentNeeded) {
      const maxes = allocateSegmentMaxes(truncated, availableForTruncContent);

      truncated.forEach((_, localIdx) => {
        const globalNonEmptyIdx = truncateFrom + localIdx;
        const origIdx = nonEmptyToOriginal[globalNonEmptyIdx];
        result[origIdx] = truncateWithEllipsis(
          segments[origIdx],
          maxes[localIdx]
        );
      });

      return result;
    }
  }

  // Fallback: truncate all proportionally
  const truncSepLen = joinWithSeparatorCount(nonEmptySegments.length);
  const availableForTruncContent = maxLength - truncSepLen;

  if (availableForTruncContent <= 3) {
    const firstIdx = nonEmptyToOriginal[0];
    result[firstIdx] = truncateWithEllipsis(
      nonEmptySegments[0],
      Math.max(1, availableForTruncContent)
    );
    for (let i = 1; i < nonEmptyToOriginal.length; i++) {
      result[nonEmptyToOriginal[i]] = "";
    }
    return result;
  }

  const maxes = allocateSegmentMaxes(
    nonEmptySegments,
    availableForTruncContent
  );

  nonEmptySegments.forEach((_, i) => {
    const origIdx = nonEmptyToOriginal[i];
    result[origIdx] = truncateWithEllipsis(segments[origIdx], maxes[i]);
  });

  return result;
}

export function truncateOfficeAndFloor(
  officeName: string,
  floorName: string,
  areaName?: string,
  maxLength = 140
): { officeName: string; floorName: string; areaName: string } {
  const segments = [officeName, floorName];
  if (areaName) {
    segments.push(areaName);
  }
  const truncated = truncateSegments(segments, maxLength, SEPARATOR_VISUAL);

  return {
    officeName: truncated[0],
    floorName: truncated[1],
    areaName: truncated[2] || ""
  };
}
